import { Box, Chip, Tab, Tabs, Typography } from '@mui/material'
import { useAPI } from 'contexts/APIProvider'
import { useSnackBarAlert } from 'contexts/SnackBarAlertProvider'
import BillingAccount from 'logic/BillingAccount'
import React from 'react'
import { Outlet, useMatch, useNavigate, useParams } from 'react-router-dom'
import AdminBillingAccountDetailContext from './context'
import { 
    CashBalanceTransactionSearchForm, 
    InvoiceSearchForm, 
    PracticeSearchForm
} from 'types/interfaces'
import update from 'immutability-helper'
import Loading from 'components/Loading'
import SplitButton from 'components/SplitButton'

const AdminBillingAccountDetail: React.FC = () => {
    const { api } = useAPI()
    const { showAlert } = useSnackBarAlert()
    const params = useParams()
    const navigate = useNavigate()
    const match = useMatch({ path: '/admin/billing-accounts/:billingAccountId/:rest', caseSensitive: false, end: false })
    
    const billingAccountId = React.useMemo(() => Number(params.billingAccountId), [params.billingAccountId])
    const [billingAccount, setBillingAccount] = React.useState<BillingAccount>()

    const [practiceSearchForm, setPracticeSearchForm] = React.useState<PracticeSearchForm>({ billing_account_id: billingAccountId })
    const [invoiceSearchForm, setInvoiceSearchForm] = React.useState<InvoiceSearchForm>({ billing_account_id: billingAccountId })
    const [cashBalanceTransactionSearchForm, setCashBalanceTransactionSearchForm] = React.useState<CashBalanceTransactionSearchForm>({ billing_account_id: billingAccountId })

    const updatePracticeSearchForm = React.useCallback((name: string, value: any) => {
        setPracticeSearchForm(update(practiceSearchForm, { [name]: { $set: value } }))
    }, [practiceSearchForm])
    
    const updateInvoiceSearchForm = React.useCallback((name: string, value: any) => {
        setInvoiceSearchForm(update(invoiceSearchForm, { [name]: { $set: value } }))
    }, [invoiceSearchForm])

    const updateCashBalanceTransactionSearchForm = React.useCallback((name: string, value: any) => {
        setCashBalanceTransactionSearchForm(update(cashBalanceTransactionSearchForm, { [name]: { $set: value } }))
    }, [cashBalanceTransactionSearchForm])

    const fetchBillingAccount = React.useCallback(async () => {
        try {
            const result = await api.getBillingAccount(billingAccountId)
            setBillingAccount(result)
        } catch (error) {
            showAlert('error', 'Failed to fetch billing account')
        }
    }, [api, billingAccountId, showAlert])

    const selectedTab = React.useMemo(() => {
        return match?.params.rest ?? 'cash-balance-transactions'
    }, [match?.params.rest])

    React.useEffect(() => {
        fetchBillingAccount()
    }, [fetchBillingAccount])

    const contextValue = {
        // data
        billingAccount,

        // search forms
        practiceSearchForm,
        invoiceSearchForm,
        cashBalanceTransactionSearchForm,

        // search form update functions
        updatePracticeSearchForm,
        updateInvoiceSearchForm,
        updateCashBalanceTransactionSearchForm,
        setPracticeSearchForm,
        setInvoiceSearchForm,
        setCashBalanceTransactionSearchForm,

        // fetch functions
        fetchBillingAccount,
    }

    if (!billingAccount) return <Loading />

    return (
        <AdminBillingAccountDetailContext.Provider value={contextValue}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: 'solid thin black', paddingBottom: 1 }}>
                    <Typography variant='h1'>{billingAccount.name} (#{billingAccount.id})</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, flexWrap: 'wrap' }}>
                        <Chip size='small' label={`Balance: £${(billingAccount.ending_balance / 100).toFixed(2)}`} color='primary'/>
                        <Chip size='small' label={billingAccount.customer_id} />
                        <Chip size='small' label={billingAccount.email} />
                        <Chip size='small' label={billingAccount.practice_group_label} />
                        <Chip size='small' label={billingAccount.paymentOptionLabel} />
                        {billingAccount.pay_by_invoice ? (
                            <React.Fragment>
                                <Chip size='small' label={`Account Number: ${billingAccount.account_number}`} />
                                <Chip size='small' label={`Sort Code: ${billingAccount.sort_code}`} />
                                <Chip 
                                    size='small'
                                    color={billingAccount.should_reconcile_invoices ? 'success' : 'error'}
                                    label={`Invoice Reconciliation: ${billingAccount.should_reconcile_invoices ? 'ON' : 'OFF'}`} 
                                />
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                <Chip size='small' label={billingAccount.payment_method_label} />
                                {billingAccount.payment_method_error ? (
                                    <Chip 
                                        size='small' 
                                        label={billingAccount.payment_method_error} 
                                        color='error' 
                                    />
                                ) : null}
                            </React.Fragment>
                        )}
                    </Box>
                    {/* <Box sx={{ flexGrow: 1 }} />
                    <SplitButton options={[{
                        value: 'reset-card',
                        label: 'Reset Card',
                        onClick: () => Promise.resolve() // TODO: Implement
                    }, {
                        value: billingAccount.should_reconcile_invoices ? 'pause-invoice-reconciliation' : 'resume-invoice-reconciliation',
                        label: billingAccount.should_reconcile_invoices ? 'Pause Invoice Reconciliation' : 'Resume Invoice Reconciliation',
                        onClick: () => Promise.resolve() // TODO: Implement
                    }]} /> */}
                </Box>
                <Tabs
                    value={selectedTab}
                    variant="scrollable"
                    scrollButtons="auto">
                    <Tab label='Bank Transfers' value='cash-balance-transactions' onClick={() => navigate('cash-balance-transactions')}/>
                    <Tab label='Invoices' value='invoices' onClick={() => navigate('invoices')}/>
                    <Tab label='Practices' value='practices' onClick={() => navigate('practices')}/>
                    <Tab label='Invoice Late Fees' value='late-invoices' onClick={() => navigate('late-invoices')}/>
                </Tabs>
                <Outlet />
            </Box>
        </AdminBillingAccountDetailContext.Provider>
    )
}

export default AdminBillingAccountDetail